/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-07-25 19:58:17
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React, { Component } from 'react';
import {
  Card,
  ButtonGroup,
  Button,
  Icon,
  SkeletonDisplayText,
  Link,
  TextStyle
} from '@shopify/polaris';
import { MobileVerticalDotsMajor, EditMajor, DeleteMajor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import OptionModal from 'src/components/containers/filter-tree/item/option-modal';
import OptionsTableCell from './options-table-cell';
import { getFilterOptionValue } from 'src/helpers/filter-tree-helper';
import ConfirmRemoveModal from 'src/components/views/filter-tree/confirm-remove-modal';
import empty from 'src/assets/images/filter-tree/empty-option.png';
import { URL } from 'src/constants/links';

type Props = {
  items: any[],
  isDefaultSearch: boolean,
  numberOfFilterOptionsForSearch: number,
  numberOfFilterOptionsForCustomFilter: number
};

type ComposedProps = Props & WithI18nProps;
// DONE
class OptionsTable extends Component<ComposedProps, State> {
  state = {
    activeModal: false,
    activeModalRemove: false,
    filterOption: {},
    index: -1,
    selectedId: -1
  };

  onDragEnd = (result: DropResult) => {
    const { fields, updateFilterOptionField } = this.props;

    // dropped outside the list
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }

    // no movement
    if (result.destination.index === result.source.index) {
      return;
    }

    fields.move(result.source.index, result.destination.index);
    fields.map((item, index) => updateFilterOptionField(index, 'position', index));
  };

  render() {
    const {
      optionsValue,
      loading,
      isDefaultSearch,
      numberOfFilterOptionsForSearch,
      numberOfFilterOptionsForCustomFilter,
      i18n
    } = this.props;
    const { activeModal, filterOption, index } = this.state;
    const isExceededOptions =
      (isDefaultSearch && optionsValue.length >= numberOfFilterOptionsForSearch) ||
      (!isDefaultSearch && optionsValue.length >= numberOfFilterOptionsForCustomFilter);

    const actions = isExceededOptions
      ? []
      : [
          {
            content: i18n.translate('AdminHTML.FilterTree.Item.optionTable_Actions_Content'),
            onAction: () => this.handleAddFilterOption()
          }
        ];

    return (
      <Card
        title={i18n.translate('AdminHTML.FilterTree.Item.optionTable_CardTitle')}
        actions={actions}>
        <Card.Section>
          <DragDropContext onDragEnd={this.onDragEnd}>
            <div className="Polaris-DataTable BC-DataTable__FilterOptions">
              <div className="Polaris-DataTable__ScrollContainer" style={{ marginLeft: 0 }}>
                <table className="Polaris-DataTable__Table">
                  <thead>
                    <tr>
                      <th
                        className={`Polaris-DataTable__Cell Polaris-DataTable__Cell--firstColumn Polaris-DataTable__Cell--header ${
                          optionsValue.length === 0 && 'EmptyCell'
                        }`}
                      />
                      <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                        {i18n.translate('AdminHTML.FilterTree.Common.status')}
                      </th>
                      <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                        {i18n.translate('AdminHTML.FilterTree.Item.optionTable_TableHeading_Label')}
                      </th>
                      <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                        {i18n.translate('AdminHTML.FilterTree.Item.optionTable_TableHeading_Type')}
                      </th>
                      <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                        {i18n.translate(
                          'AdminHTML.FilterTree.Item.optionTable_TableHeading_DisplayType'
                        )}
                      </th>
                      <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                        {i18n.translate('AdminHTML.FilterTree.Item.optionTable_TableHeading_Value')}
                      </th>
                      <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header Polaris-DataTable__Cell--numeric">
                        {i18n.translate('AdminHTML.FilterTree.Common.actions')}
                      </th>
                    </tr>
                  </thead>
                  {loading ? (
                    <tbody>
                      {[...Array(3).keys()].map((item, key) => (
                        <tr key={key}>
                          {[...Array(7).keys()].map((item, key) => (
                            <td className="Polaris-DataTable__Cell" key={key}>
                              <SkeletonDisplayText size="small" />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  ) : !_.isEmpty(optionsValue) ? (
                    <Droppable droppableId="table">
                      {provided => (
                        <tbody ref={provided.innerRef} {...provided.droppableProps}>
                          {optionsValue.map((item, key) => {
                            return (
                              // Enable drag for per option row
                              <Draggable
                                draggableId={item.filterOptionId}
                                key={item.filterOptionId}
                                index={key}>
                                {(provided, snapshot) => (
                                  <tr
                                    className="Polaris-DataTable__TableRow"
                                    ref={provided.innerRef}
                                    {...provided.dragHandleProps}
                                    {...provided.draggableProps}>
                                    {/* Drag icon */}
                                    <OptionsTableCell
                                      isDragOccurring={snapshot.isDragging}
                                      className="Polaris-DataTable__Cell Polaris-DataTable__Cell--firstColumn">
                                      <Icon color="subdued" source={MobileVerticalDotsMajor} />
                                    </OptionsTableCell>

                                    {/* Status */}
                                    <OptionsTableCell
                                      isDragOccurring={snapshot.isDragging}
                                      className="Polaris-DataTable__Cell">
                                      <label className="BC-Checkbox-Status">
                                        <input
                                          type="checkbox"
                                          checked={item.status === 'active'}
                                          onChange={e => this.updateStatus(e, key)}
                                        />
                                        <span />
                                      </label>
                                    </OptionsTableCell>

                                    {/* Filter label*/}
                                    <OptionsTableCell
                                      isDragOccurring={snapshot.isDragging}
                                      className="Polaris-DataTable__Cell">
                                      <Link
                                        removeUnderline
                                        onClick={() => {
                                          this.handleViewFilterOption(key);
                                        }}>
                                        {item.label}
                                      </Link>
                                    </OptionsTableCell>

                                    {/* Filter type */}
                                    <OptionsTableCell
                                      isDragOccurring={snapshot.isDragging}
                                      className="Polaris-DataTable__Cell">
                                      {item.filterType}
                                    </OptionsTableCell>

                                    {/* Display type */}
                                    <OptionsTableCell
                                      isDragOccurring={snapshot.isDragging}
                                      className="Polaris-DataTable__Cell">
                                      {item.displayType}
                                    </OptionsTableCell>

                                    {/* Value */}
                                    <OptionsTableCell
                                      isDragOccurring={snapshot.isDragging}
                                      className="Polaris-DataTable__Cell">
                                      {this.shortenString(getFilterOptionValue(item, i18n))}
                                    </OptionsTableCell>

                                    {/* Action */}
                                    <OptionsTableCell
                                      isDragOccurring={snapshot.isDragging}
                                      className="Polaris-DataTable__Cell Polaris-DataTable__Cell-Action">
                                      {this.renderAction(key.toString())}
                                    </OptionsTableCell>
                                  </tr>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </tbody>
                      )}
                    </Droppable>
                  ) : (
                    <tbody />
                  )}
                </table>
                {_.isEmpty(optionsValue) && !loading ? (
                  <div className="BC-EmptyOption__FilterTree">
                    <img
                      src={empty}
                      alt={i18n.translate('AdminHTML.FilterTree.Item.optionTable_ImgAlt')}
                    />
                    <p>
                      {i18n.translate(
                        'AdminHTML.FilterTree.Item.optionTable_EmptyOption_Paragraph'
                      )}
                    </p>
                  </div>
                ) : (
                  ''
                )}
              </div>
            </div>
          </DragDropContext>
        </Card.Section>
        <Card.Section>
          {isExceededOptions ? (
            <div>
              <p>
                <TextStyle variation="negative">
                  <TextStyle variation="strong">
                    {i18n.translate('AdminHTML.FilterTree.Item.optionTable_Notice')}
                  </TextStyle>
                </TextStyle>{' '}
                {i18n.translate('AdminHTML.FilterTree.Item.optionTable_Paragraph', {
                  t1: (
                    <TextStyle variation="strong">
                      {i18n.translate('AdminHTML.FilterTree.Item.optionTable_Paragraph_t1', {
                        nt_number: isDefaultSearch
                          ? numberOfFilterOptionsForSearch
                          : numberOfFilterOptionsForCustomFilter
                      })}
                    </TextStyle>
                  ),
                  t2: (
                    <Link external url={URL['FilterOptionBestPractice']}>
                      {i18n.translate('AdminHTML.FilterTree.Item.optionTable_Paragraph_t2')}
                    </Link>
                  )
                })}
              </p>
            </div>
          ) : (
            <div style={{ textAlign: 'right' }}>
              <Button plain onClick={() => this.handleAddFilterOption()}>
                {i18n.translate('AdminHTML.FilterTree.Item.optionTable_Actions_Content')}
              </Button>
            </div>
          )}
        </Card.Section>
        <OptionModal
          active={activeModal}
          handleChange={this.handleActiveOptionModal}
          addFilterOption={this.addFilterOption}
          updateFilterOption={this.updateFilterOption}
          filterOption={filterOption}
          index={index}
        />
        <ConfirmRemoveModal
          active={this.state.activeModalRemove}
          handleChange={this.handleActiveOptionModalRemove}
          handleRemove={this.deleteFilterOption}
          title={i18n.translate('AdminHTML.FilterTree.Item.optionTable_ConfirmRemoveModal_Title')}
          content={i18n.translate(
            'AdminHTML.FilterTree.Item.optionTable_ConfirmRemoveModal_Content'
          )}
        />
      </Card>
    );
  }

  // Render action button for per filter
  renderAction = (index: number) => {
    return (
      <ButtonGroup segmented>
        <Button size="slim" icon={EditMajor} onClick={() => this.handleViewFilterOption(index)} />
        <Button
          size="slim"
          icon={DeleteMajor}
          onClick={() => {
            this.updateSelected(index);
            this.handleActiveOptionModalRemove(index);
          }}
        />
      </ButtonGroup>
    );
  };

  // Handle open/close modal
  handleActiveOptionModal = () => {
    this.setState(({ activeModal }) => ({
      activeModal: !activeModal
    }));

    // Cleanup multi-level values
    this.props.resetMultiLevelStates();
  };

  // Update selected id
  updateSelected = index => {
    this.setState(({ selectedId }) => ({
      selectedId: index
    }));
  };

  // Handle open/close modal delete
  handleActiveOptionModalRemove = () => {
    this.setState(({ activeModalRemove }) => ({
      activeModalRemove: !activeModalRemove
    }));
  };
  // Handle add option filter button
  handleAddFilterOption = () => {
    this.setState({ filterOption: {}, index: -1 });

    // Cleanup multi-level values
    this.props.resetMultiLevelStates();

    // Open modal
    this.handleActiveOptionModal();
  };

  // Handle view/edit filter button
  handleViewFilterOption = index => {
    const { fields } = this.props;

    this.setState({ filterOption: fields.get(index), index });
    this.handleActiveOptionModal();
  };

  // Shorten string if length > 140 character
  shortenString = (value: string) => {
    if (value && value.length > 140) {
      let newValue = value.slice(0, 140);

      const lastIndex = newValue.lastIndexOf('|');
      newValue = newValue.slice(0, lastIndex) + '...';

      return newValue;
    }
    return value;
  };

  updateStatus = (e, index) => {
    const { updateFilterOptionField } = this.props;
    updateFilterOptionField(index, 'status', e.target.checked ? 'active' : 'disabled');
  };

  updateFilterOption = (data, index) => {
    const { updateFilterOption } = this.props;
    updateFilterOption(index, data);
  };

  addFilterOption = data => {
    const { fields } = this.props;
    fields.push({ ...data, position: fields.length });
  };

  deleteFilterOption = () => {
    const { fields } = this.props;
    const { selectedId } = this.state;

    fields.remove(selectedId);
    this.handleActiveOptionModalRemove();
  };
}
export default withI18n()(OptionsTable);
